import React, { useEffect, useState } from "react";
import { FastField, Field, useFormikContext } from "formik";
import { Button, CircularProgress, Grid, TextField } from "@mui/material";
import DropdownSelector from "../../atoms/Common/DropdownSelector.tsx";
import { getCatalogue } from "../../api/common/catalogues.ts";
import { getProviderByTaxId } from "../../api/providers";
import { useSnack } from "../../services/SnakbarProvider";
import { useCompany } from "../../contexts/CompanyContext";
import { LGNumber } from "../../atoms/Inputs/LGNumber.tsx";

const ProviderDetailsForm = ({ errors, touched, isPersonaMoral, taxRegimes, onProviderTypeChange }) => {
  const { values, setFieldValue } = useFormikContext();
  const { openSnack } = useSnack();
  const [loadingProvider, setLoadingProvider] = useState(false);
  const { selectedCompany } = useCompany();
  const [selectedProviderType, setSelectedProviderType] = useState(null);
  const [selectedProviderScope, setSelectedProviderScope] = useState(null);
  const [isPersonaMoralLocal, setIsPersonaMoralLocal] =
    useState(isPersonaMoral);
  const [taxRegimesLocal, setTaxRegimesLocal] = useState(taxRegimes);

  useEffect(() => {
    setTaxRegimesLocal(taxRegimes);
  }, [taxRegimes]);

  const fetchProviderByTaxId = async (taxId) => {
    setLoadingProvider(true);
    try {
      const provider = await getProviderByTaxId(taxId);
      if (
        provider.data.accounts?.some(
          (account) => account.companyId === selectedCompany.id
        )
      ) {
        openSnack("Ya existe el proveedor en esta empresa", "warning");
        setLoadingProvider(false);
        return;
      } else {
        setFieldValue("provider", provider.data);
        setLoadingProvider(false);
        openSnack(`Proveedor encontrado en otra empresa`, "success");
      }
    } catch (error) {
      setLoadingProvider(false);
      openSnack("No se encontró el proveedor", "warning");
    }
  };

  useEffect(() => {
    if (!values.provider.code) {
      const legalNameParts = values.provider.legalName.split(" ");
      const firstWord = legalNameParts[0]?.slice(0, 7) || "";
      const secondWord = legalNameParts[1]?.slice(0, 3) || "";
      const taxIdPart = values.provider.taxId?.slice(0, 4) || "";
      const providerCode =
        `${firstWord}${secondWord}${taxIdPart}`.toUpperCase();
      setFieldValue("provider.code", providerCode);
    }
  }, [values.provider?.legalName, values.provider?.taxId, setFieldValue]);

  // Check if current provider type is "Persona Moral"
  useEffect(() => {
    if (selectedProviderType) {
      setIsPersonaMoralLocal(selectedProviderType.code === "PM");
    }
  }, [selectedProviderType]);

  // Check if providerOrigin is EXTRANJERO and legalEntityType is PERSONA MORAL
  const shouldHideForeignFields = 
    selectedProviderScope?.name === "EXTRANJERO" && 
    selectedProviderType?.code === "PM";

  useEffect(() => {
    if (shouldHideForeignFields) {
      setFieldValue("provider.corporationTypeId", null);
      setFieldValue("provider.regimeId", null);
    }
  }, [shouldHideForeignFields, setFieldValue]);

  useEffect(() => {
    setIsPersonaMoralLocal(isPersonaMoral);
  }, [isPersonaMoral]);

  // Initialize selectedProviderScope and selectedProviderType from provider data if available (edit mode)
  useEffect(() => {
    if (values.provider?.providerScope) {
      setSelectedProviderScope(values.provider.providerScope);
    }
    if (values.provider?.legalEntityType) {
      setSelectedProviderType(values.provider.legalEntityType);
    }
  }, [values.provider?.providerScope, values.provider?.legalEntityType]);

  return (
    <Grid container spacing={2} alignItems={"flex-start"}>
      <Grid item xs={5}>
        <Field
          name="provider.legalName"
          label="Nombre o Razón social"
          as={TextField}
          fullWidth
          error={touched.provider?.legalName && !!errors.provider?.legalName}
        />
      </Grid>
      <Grid item xs={5}>
        <Field
          fullWidth
          name="provider.taxId"
          label="RFC / Tax ID"
          as={TextField}
          error={touched.provider?.taxId && !!errors.provider?.taxId}
          helperText={touched.provider?.taxId && errors.provider?.taxId}
        />
      </Grid>

      <Grid item xs={2}>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          sx={{ height: "100%" }}
          disabled={loadingProvider}
          onClick={async () => {
            const taxId = values.provider.taxId;
            await fetchProviderByTaxId(taxId);
          }}
        >
          {loadingProvider ? <CircularProgress size={20} /> : "Buscar por RFC"}
        </Button>
      </Grid>
      <Grid item xs={4}>
        <Field
          fullWidth
          name="provider.code"
          label="Clave"
          as={TextField}
          error={touched.provider?.code && !!errors.provider?.code}
        />
      </Grid>

      <Grid item xs={4}>
        <FastField
          name="provider.providerScopeId"
          label="Origen del proveedor"
          as={DropdownSelector}
          fetchFunction={getCatalogue}
          fetchParams={{ CatalogueName: "PROVIDER_ORIGIN" }}
          onChange={(event, value) => {
            setSelectedProviderScope(value);
          }}
          error={
            touched.provider?.providerScopeId &&
            !!errors.provider?.providerScopeId
          }
        />
      </Grid>

      <Grid item xs={4}>
        <FastField
          name="provider.legalEntityTypeId"
          label="Tipo de proveedor"
          as={DropdownSelector}
          fetchFunction={getCatalogue}
          fetchParams={{ CatalogueName: "PROVIDER_TYPE" }}
          onChange={(event, value) => {
            setSelectedProviderType(value);
            // Regimes depend on the provider type, so a manual change invalidates them
            setFieldValue("provider.regimeId", null);
            if (value?.code !== "PM") {
              setFieldValue("provider.corporationTypeId", null);
            }
            if (onProviderTypeChange) {
              onProviderTypeChange(value?.id || null);
            }
          }}
        />
      </Grid>

      {!shouldHideForeignFields && (
        <Grid item xs={isPersonaMoralLocal ? 6 : 12}>
          <Field
            name="provider.regimeId"
            label="Régimen"
            as={DropdownSelector}
            parentOptions={taxRegimesLocal || []}
            optionLabel={["code", "name"]}
            optionSeparator=" | "
            error={touched.provider?.regimeId && !!errors.provider?.regimeId}
            // helperText={touched.provider?.regimeId && errors.provider?.regimeId}
          />
        </Grid>
      )}

      {isPersonaMoralLocal && !shouldHideForeignFields && (
        <Grid item xs={6}>
          <FastField
            name="provider.corporationTypeId"
            label="Sociedad moral"
            as={DropdownSelector}
            fetchFunction={getCatalogue}
            fetchParams={{ CatalogueName: "CORPORATION_TYPE" }}
          />
        </Grid>
      )}

      <Grid item xs={6}>
        <Field
          fullWidth
          name="provider.requestedBy"
          label="Solicitado por"
          as={TextField}
          error={
            touched.provider?.requestedBy && !!errors.provider?.requestedBy
          }
        />
      </Grid>
      <Grid item xs={4}>
        <Field
          fullWidth
          name="provider.website"
          label="Sitio Web"
          as={TextField}
          error={touched.provider?.website && !!errors.provider?.website}
          helperText={touched.provider?.website && errors.provider?.website}
        />
      </Grid>
      <Grid item xs={2}>
        <Field
          fullWidth
          name="provider.creditDays"
          label="Días de Crédito"
          as={LGNumber}
          error={touched.provider?.creditDays && !!errors.provider?.creditDays}
        />
      </Grid>
      <Grid item xs={12}>
        <Field
          fullWidth
          name="provider.descripcion"
          label="Descripción"
          as={TextField}
          error={touched.provider?.descripcion && !!errors.provider?.descripcion}
        />
      </Grid>
    </Grid>
  );
};

export default ProviderDetailsForm;
